import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '../components/ui/Card';
import { Label } from '../components/ui/Label';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { MockBadge } from '../components/MockBadge';
import { apiFetch } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDateTime } from '../lib/format';
import { languageLabel } from '../lib/contactAttributes';
import { ApiError, type ConsentText, type ConsentTextKind } from '../types/api';

const KIND_LABEL: Record<ConsentTextKind, string> = {
  form: 'Signup form',
  checkout: 'Checkout box',
};

/** The shop's sending languages (core `NEWSLETTER_LANGUAGES`). */
const LANGUAGES = ['en', 'da', 'de', 'fr', 'it'] as const;

/** Which current text a version can be: one per kind and language (`''` = the default text). */
function currentKey(kind: ConsentTextKind, language: string | null): string {
  return `${kind}:${language ?? ''}`;
}

/**
 * Consent texts (migrations 0009, 0035): the exact wording a subscriber says
 * yes to, per version. Each text belongs to the signup form or the shop's
 * checkout box, and the newest of each kind is the one that place uses.
 * A text can be for one language (migration 0042): a page in that language
 * gets it, every other language gets the newest text without a language.
 * There is no edit: a new wording is a new version, and a used version is
 * locked.
 */
export function SamtykketeksterPage() {
  const { isAdmin } = useAuth();
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState('');
  const [kind, setKind] = useState<ConsentTextKind>('form');
  const [language, setLanguage] = useState('');
  const { data, isLoading, isError } = useQuery({
    queryKey: ['consent-texts'],
    queryFn: () => apiFetch<{ items: ConsentText[] }>('/consent-texts'),
  });

  const createMutation = useMutation({
    mutationFn: (input: { text: string; kind: ConsentTextKind; language: string | null }) =>
      apiFetch<ConsentText>('/consent-texts', { method: 'POST', body: input }),
    onSuccess: () => {
      setDraft('');
      void queryClient.invalidateQueries({ queryKey: ['consent-texts'] });
    },
  });

  const createError =
    createMutation.error instanceof ApiError
      ? (createMutation.error.body.message ?? 'Could not be created. Try again.')
      : createMutation.isError
        ? 'Could not be created. Try again.'
        : null;

  const items = data?.items ?? [];
  // Newest first, so the first item of each kind and language is the current one there.
  const currentVersions = new Set<string>();
  const seen = new Set<string>();
  for (const item of items) {
    const key = currentKey(item.kind, item.language);
    if (seen.has(key)) continue;
    seen.add(key);
    currentVersions.add(item.version);
  }

  return (
    <>
      <header className="flex items-center gap-3">
        <h1 className="m-0 text-2xl font-semibold tracking-tight">Consent texts</h1>
        <MockBadge />
      </header>
      <p className="m-0 text-sm text-muted max-w-[640px]">
        The signup form and the shop&apos;s checkout box each use their newest version, and it is
        stored with each consent. A version for one language is shown on pages in that language; all
        other languages get the newest version for all languages. A version that has been used
        cannot be changed or deleted. New wording is a new version.
      </p>

      {!isAdmin ? (
        <Alert>You have read-only access. Contact an administrator to create a new version.</Alert>
      ) : null}

      {isAdmin ? (
        <Card className="p-4 flex flex-col gap-3 max-w-[640px]">
          <h2 className="text-sm font-semibold m-0">New version</h2>
          <fieldset className="m-0 p-0 border-0 flex flex-col gap-1.5">
            <legend className="text-sm font-medium mb-1.5">Shown in</legend>
            <div className="flex flex-wrap gap-4 text-sm">
              {(['form', 'checkout'] as const).map((k) => (
                <label key={k} className="inline-flex items-center gap-2">
                  <input
                    type="radio"
                    name="ct-kind"
                    value={k}
                    checked={kind === k}
                    onChange={() => setKind(k)}
                  />
                  {KIND_LABEL[k]}
                </label>
              ))}
            </div>
          </fieldset>
          {kind === 'form' ? (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="ct-language">Language</Label>
              <select
                id="ct-language"
                className="h-[38px] max-w-[280px] rounded-[9px] border border-zinc-300 bg-card px-3 text-sm"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
              >
                <option value="">All languages (default)</option>
                {LANGUAGES.map((code) => (
                  <option key={code} value={code}>
                    {languageLabel(code)}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="ct-text">Consent text</Label>
            <textarea
              id="ct-text"
              className="w-full min-h-[110px] rounded-[9px] border border-zinc-300 px-3 py-2 text-sm"
              value={draft}
              maxLength={5000}
              onChange={(e) => setDraft(e.target.value)}
            />
          </div>
          {createError ? <Alert>{createError}</Alert> : null}
          {createMutation.isSuccess ? (
            <div role="status" className="text-success-text text-sm">
              Version {createMutation.data.version} has been created.
              {createMutation.data.kind === 'checkout'
                ? ` Put ${createMutation.data.version} in NL_CHECKOUT_OPTIN_CONSENT_VERSION in the shop's wp-config.php.`
                : null}
            </div>
          ) : null}
          <Button
            className="self-end"
            disabled={draft.trim() === '' || createMutation.isPending}
            onClick={() =>
              createMutation.mutate({
                text: draft,
                kind,
                language: kind === 'form' && language ? language : null,
              })
            }
          >
            {createMutation.isPending ? 'Creating…' : 'Create new version'}
          </Button>
        </Card>
      ) : null}

      {isLoading ? <div className="text-muted">Loading consent texts…</div> : null}
      {isError ? <Alert>The consent texts could not be loaded.</Alert> : null}
      {!isLoading && !isError && items.length === 0 ? (
        <p className="text-muted text-sm m-0">
          No consent text yet. Signups are saved without a version until one has been created.
        </p>
      ) : null}

      <ul className="m-0 p-0 list-none flex flex-col gap-3 max-w-[640px]">
        {items.map((item) => (
          <li key={item.version}>
            <Card className="p-4 flex flex-col gap-2">
              <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
                <span className="font-semibold">
                  {item.version}
                  <span className="ml-2 text-xs font-normal text-muted">
                    {KIND_LABEL[item.kind]} ·{' '}
                    {item.language ? languageLabel(item.language) : 'All languages'}
                  </span>
                  {currentVersions.has(item.version) ? (
                    <span className="ml-2 text-xs text-muted">(current)</span>
                  ) : null}
                </span>
                <span className="text-xs text-muted">
                  {item.locked && item.firstUsedAt !== null
                    ? `Locked · first used ${formatDateTime(item.firstUsedAt)}`
                    : 'Not used yet'}
                </span>
              </div>
              <p className="m-0 text-[13px] whitespace-pre-wrap">{item.text}</p>
              <div className="text-xs text-muted">Created {formatDateTime(item.createdAt)}</div>
            </Card>
          </li>
        ))}
      </ul>
    </>
  );
}
